<template>
  <b-modal :title="helper.trans('labels.ban_user')"
           :modelValue="isVisible"
           footer-class="d-flex justify-content-end"
           @update:modelValue="isVisible = $event">
    <template #default>
      <div v-if="userId" class="mb-3">
        <span class="text-muted">{{ helper.trans('labels.user_id') }}: </span>
        <span class="font-monospace">{{ userId }}</span>
      </div>
      <div class="mb-3">
        <div class="form-check">
          <input type="checkbox"
                 class="form-check-input"
                 id="banPermanent"
                 v-model="permanent">
          <label class="form-check-label" for="banPermanent">
            {{ helper.trans('labels.permanent_ban') }}
          </label>
        </div>
      </div>
      <div v-if="!permanent" class="mb-3">
        <label class="form-label" for="banDuration">{{ helper.trans('labels.duration_seconds') }}</label>
        <input type="number"
               id="banDuration"
               class="form-control"
               min="1"
               v-model.number="durationSeconds"
               :placeholder="helper.trans('labels.duration_seconds')">
      </div>
      <div v-if="validationError" class="alert alert-danger" role="alert">
        {{ validationError }}
      </div>
    </template>
    <template #footer>
      <button type="button" @click.prevent="hide" class="btn btn-outline-dark btn-sm text-capitalize-first">
        {{ helper.trans('button.cancel') }}
      </button>
      <LoaderWrapper ref="loader" :zoom="0.5">
        <button type="button" @click.prevent="submit" class="btn btn-outline-danger btn-sm text-capitalize-first">
          {{ helper.trans('button.ban') }}
        </button>
      </LoaderWrapper>
    </template>
  </b-modal>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue';
import { helper } from '@/service/CommonHelper';
import { api } from '@/service/APIClient';
import { BModal } from 'bootstrap-vue-3';
import LoaderWrapper from '@/components/LoaderWrapper.vue';
import { useNotification } from '@kyvg/vue3-notification';
import MerchantUserBanRequestDTO from '@/models/MerchantUserBanRequestDTO';

const notification = useNotification();

export default defineComponent({
  components: {
    BModal,
    LoaderWrapper,
  },
  emits: ['banned'],
  setup(_, { emit }) {
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null);
    const isVisible = ref(false);
    const storeId = ref<number | null>(null);
    const userId = ref<string | null>(null);
    const permanent = ref(true);
    const durationSeconds = ref<number | ''>(86400);
    const validationError = ref('');

    const show = (sid: number, uid: string) => {
      storeId.value = sid;
      userId.value = uid;
      permanent.value = true;
      durationSeconds.value = 86400;
      validationError.value = '';
      isVisible.value = true;
    };

    const hide = () => {
      isVisible.value = false;
      storeId.value = null;
      userId.value = null;
      validationError.value = '';
    };

    watch(permanent, (v) => {
      if (v) {
        validationError.value = '';
      }
    });

    const submit = async () => {
      if (storeId.value == null || userId.value == null) return;

      if (!permanent.value) {
        const sec = durationSeconds.value;
        if (sec === '' || sec == null || sec < 1) {
          validationError.value = helper.trans('labels.duration_seconds_required');
          return;
        }
      }
      validationError.value = '';

      loader.value?.show();
      try {
        const body: MerchantUserBanRequestDTO = {
          storeId: storeId.value,
          userId: userId.value,
          permanent: permanent.value,
          ...(permanent.value ? {} : { durationSeconds: Number(durationSeconds.value) }),
        };
        await api.call.statistic.merchantUsersBan(body);
        notification.notify({ title: helper.trans('labels.ban_success') || 'User banned', type: 'success' });
        hide();
        emit('banned');
      } catch (e: any) {
        const msg = e?.response?.data?.message || e?.message || helper.trans('labels.error');
        validationError.value = msg;
        notification.notify({ title: msg, type: 'error' });
      } finally {
        loader.value?.hide();
      }
    };

    return {
      loader,
      isVisible,
      userId,
      permanent,
      durationSeconds,
      validationError,
      helper,
      show,
      hide,
      submit,
    };
  },
});
</script>
